import React from 'react';
import { Leaf, Quote, ArrowRight } from 'lucide-react';
import { formatMoney, onAccent, type StorefrontConfig } from '../types';

/**
 * Studio — beauty, wellness and services.
 *
 * Convention research (Dribbble, skincare/cosmetics e-commerce): warm muted
 * neutrals rather than white, a serif or italic display face against a clean
 * sans, editorial claims stated plainly ("what it does, who it's for"),
 * testimonials given real estate, and routines presented as numbered steps.
 * The catalogue is small and each product needs an argument, so the layout
 * gives every item room for one instead of packing a grid.
 *
 * What that means here: a warm-tinted hero, a numbered routine strip, and
 * product rows — alternating sides — with the blurb carrying equal weight to
 * the price. `blurb` is the field this template lives on; Boutique ignores it
 * and Market hides it entirely.
 */
export default function Studio({ config }: { config: StorefrontConfig }) {
  const ink = '#2A2622';
  const sand = '#F4EFE8';
  const accentText = onAccent(config.accent);

  return (
    <div style={{ background: sand, color: ink, fontFamily: 'var(--font-family)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px clamp(16px, 5vw, 64px)',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 18, letterSpacing: '-0.01em' }}>{config.name}</span>
        <nav style={{ display: 'flex', gap: 'clamp(12px, 2.5vw, 26px)', flexWrap: 'wrap' }}>
          {config.collections.map((c) => (
            <span key={c} style={{ fontSize: 13, opacity: 0.65 }}>
              {c}
            </span>
          ))}
        </nav>
      </header>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 'clamp(20px, 4vw, 56px)',
          alignItems: 'center',
          padding: 'clamp(20px, 4vw, 56px) clamp(16px, 5vw, 64px) clamp(36px, 6vw, 80px)',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            <Leaf size={13} /> {config.category}
          </span>
          <h1
            style={{
              // Serif is the whole tonal difference from Boutique's sans —
              // this vertical reads as care, not as streetwear.
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(34px, 5.5vw, 60px)',
              lineHeight: 1.06,
              fontWeight: 400,
              letterSpacing: '-0.02em',
              margin: '14px 0 16px',
            }}
          >
            {config.bio}
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.65, opacity: 0.72, marginBottom: 26 }}>
            Small batches, short ingredient lists, and formulas tested on the skin they were made for.
          </p>
          <button
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              height: 50,
              padding: '0 26px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: config.accent,
              color: accentText,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {config.showPrices ? 'Shop the range' : 'Book a consultation'}
            <ArrowRight size={16} />
          </button>
        </div>

        <div
          style={{
            aspectRatio: '4 / 3',
            borderRadius: 20,
            background: `linear-gradient(140deg, ${config.products[0]?.swatch[0] ?? '#E6D8C6'}, ${
              config.products[3]?.swatch[1] ?? '#957E66'
            })`,
          }}
        />
      </section>

      {/* Routine strip — the "how do I use this" answer that this vertical
          always front-loads, built from the merchant's own collections. */}
      <section
        style={{
          background: '#FFFFFF',
          padding: 'clamp(28px, 5vw, 56px) clamp(16px, 5vw, 64px)',
        }}
      >
        <h2
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(22px, 3vw, 30px)',
            fontWeight: 400,
            marginBottom: 24,
          }}
        >
          The routine
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
            gap: 'clamp(14px, 2vw, 28px)',
          }}
        >
          {config.collections.map((step, i) => (
            <div key={step} style={{ display: 'grid', gap: 8 }}>
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  background: config.accent,
                  color: accentText,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {i + 1}
              </span>
              <strong style={{ fontSize: 15 }}>{step}</strong>
              <span style={{ fontSize: 13, opacity: 0.6, lineHeight: 1.5 }}>
                {config.products.find((p) => p.category === step)?.name ?? 'Coming soon'}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: 'clamp(36px, 6vw, 80px) clamp(16px, 5vw, 64px)', display: 'grid', gap: 'clamp(24px, 4vw, 56px)' }}>
        {config.products.map((p, i) => (
          <article
            key={p.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
              gap: 'clamp(16px, 3vw, 44px)',
              alignItems: 'center',
              // Alternating sides keeps a four-product page from reading as a
              // list; at one column the order flag is ignored anyway.
              direction: i % 2 === 1 ? 'rtl' : 'ltr',
            }}
          >
            <div
              style={{
                direction: 'ltr',
                aspectRatio: '5 / 4',
                borderRadius: 18,
                background: `linear-gradient(145deg, ${p.swatch[0]}, ${p.swatch[1]})`,
              }}
            />
            <div style={{ direction: 'ltr', maxWidth: 460 }}>
              {p.badge ? (
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    background: config.accent,
                    color: accentText,
                    padding: '5px 10px',
                    borderRadius: 999,
                    marginBottom: 12,
                  }}
                >
                  {p.badge}
                </span>
              ) : null}
              <h3
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: 'clamp(22px, 3vw, 30px)',
                  fontWeight: 400,
                  marginBottom: 10,
                }}
              >
                {p.name}
              </h3>
              <p style={{ fontSize: 15, lineHeight: 1.65, opacity: 0.72, marginBottom: 18 }}>{p.blurb}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                {config.showPrices ? (
                  <strong style={{ fontSize: 18 }}>{formatMoney(p.price, config.currency)}</strong>
                ) : null}
                <button
                  type="button"
                  style={{
                    height: 44,
                    padding: '0 22px',
                    borderRadius: 999,
                    cursor: 'pointer',
                    background: 'transparent',
                    border: `1px solid ${ink}33`,
                    color: ink,
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {config.showPrices ? 'Add to bag' : 'Enquire'}
                </button>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section
        style={{
          background: config.accent,
          color: accentText,
          padding: 'clamp(36px, 6vw, 76px) clamp(16px, 5vw, 64px)',
          display: 'grid',
          placeItems: 'center',
          textAlign: 'center',
        }}
      >
        <Quote size={22} style={{ opacity: 0.7, marginBottom: 14 }} />
        <p
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(19px, 2.6vw, 28px)',
            lineHeight: 1.45,
            maxWidth: 720,
            fontWeight: 400,
          }}
        >
          “Three months in and the marks I'd had for years have genuinely faded. I've stopped looking for
          anything else.”
        </p>
        <span style={{ marginTop: 16, fontSize: 13, opacity: 0.8 }}>Adaeze · Lagos</span>
      </section>

      <footer
        style={{
          padding: 'clamp(24px, 4vw, 48px) clamp(16px, 5vw, 64px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 20,
          justifyContent: 'space-between',
          fontSize: 13,
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <strong>{config.name}</strong>
          <span style={{ opacity: 0.6 }}>{config.location}</span>
          <span style={{ opacity: 0.6 }}>{config.hours}</span>
        </div>
        <span style={{ opacity: 0.45 }}>{config.domain}</span>
      </footer>
    </div>
  );
}
